import { Component } from '@theme/component';
import { isClickedOutside, isMobileBreakpoint, mediaQueryLarge, onAnimationEnd } from '@theme/utilities';

/**
 * A hotspot on a shoppable image.
 *
 * One dialog serves both breakpoints: on desktop it opens non-modal, anchored under the dot
 * (hover, or click for keyboard users), and stays open while the pointer is on the dot or the
 * dialog, with a short grace period for the crossing between them; on mobile it opens modal,
 * as a bottom sheet. The two presentations are told apart in CSS by `:modal`, so nothing here
 * toggles layout classes.
 *
 * Placement needs no measuring: the component spans the whole image, so the dialog's
 * percentage offsets resolve against the image and CSS `clamp()` keeps it in frame
 * horizontally. Whether it sits above or below the dot is decided in Liquid from the
 * hotspot's vertical position.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The hotspot's dialog.
 * @property {HTMLButtonElement} trigger - The dot the shopper interacts with.
 *
 * @extends Component<Refs>
 */
export class ShoppableHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];

  /** @type {number | null} */
  #hoverTimer = null;

  /** @type {number | null} */
  #closeTimer = null;

  /** Whether hover listeners are currently attached. */
  #hoverListenersAttached = false;

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.#handleBreakpointChange();

    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.#removeHoverListeners();
    this.#removeDismissListeners();
    this.#unlockScroll();

    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Opens the dialog. Hover covers the common desktop case, but a click still has to work for
   * keyboard and touch users. It deliberately does not toggle: on desktop the hover has usually
   * opened the dialog already, and toggling closed the popover under the shopper's cursor just
   * as they went to click it. Closing is by Escape, the close button, or a click outside.
   *
   * @param {MouseEvent} event - The click event.
   */
  handleTriggerClick = (event) => {
    event.preventDefault();
    this.open();
  };

  /**
   * Opens the dialog — modal on mobile, non-modal on desktop.
   */
  open = () => {
    const { dialog } = this.refs;

    this.#clearCloseTimer();

    if (dialog.open) return;

    // Only one hotspot on the page may be open at a time.
    for (const hotspot of document.querySelectorAll('shoppable-hotspot-component')) {
      if (hotspot !== this && hotspot instanceof ShoppableHotspotComponent) hotspot.close();
    }

    if (isMobileBreakpoint()) {
      document.documentElement.setAttribute('scroll-lock', '');
      dialog.showModal();
    } else {
      dialog.show();
    }

    document.addEventListener('click', this.#lightDismiss);
    document.addEventListener('keydown', this.#handleKeydown);
  };

  /**
   * Closes the dialog once its exit animation has played.
   *
   * @returns {Promise<void>}
   */
  close = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.#removeDismissListeners();
    this.#clearHoverTimer();
    this.#clearCloseTimer();

    dialog.classList.add('dialog-closing');
    await onAnimationEnd(dialog, undefined, { subtree: false });
    dialog.classList.remove('dialog-closing');

    dialog.close();
    this.#unlockScroll();
  };

  /**
   * Escape on a modal dialog would close it natively, skipping the exit animation and the
   * scroll lock cleanup. Take it over instead.
   *
   * @param {Event} event - The cancel event.
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * Swaps hover listeners in and out as the viewport crosses the breakpoint, and closes
   * anything open — a popover and a bottom sheet are not interchangeable mid-flight.
   */
  #handleBreakpointChange = () => {
    this.#removeHoverListeners();

    if (this.refs.dialog.open) this.close();

    if (!isMobileBreakpoint()) this.#addHoverListeners();
  };

  #addHoverListeners() {
    if (this.#hoverListenersAttached) return;

    const { trigger, dialog } = this.refs;

    trigger.addEventListener('pointerenter', this.#handlePointerEnter);
    trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    dialog.addEventListener('pointerenter', this.#handleDialogPointerEnter);
    dialog.addEventListener('pointerleave', this.#handlePointerLeave);

    this.#hoverListenersAttached = true;
  }

  #removeHoverListeners() {
    if (!this.#hoverListenersAttached) return;

    const { trigger, dialog } = this.refs;

    trigger.removeEventListener('pointerenter', this.#handlePointerEnter);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    dialog.removeEventListener('pointerenter', this.#handleDialogPointerEnter);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);

    this.#clearHoverTimer();
    this.#clearCloseTimer();
    this.#hoverListenersAttached = false;
  }

  #handlePointerEnter = () => {
    // Coming back to the dot from its open dialog cancels the pending close.
    this.#clearCloseTimer();

    if (this.refs.dialog.open) return;

    this.#clearHoverTimer();
    this.#hoverTimer = setTimeout(this.open, 120);
  };

  /**
   * Reaching the dialog cancels the close that leaving the dot had scheduled.
   */
  #handleDialogPointerEnter = () => {
    this.#clearCloseTimer();
  };

  /**
   * Schedules a close on pointer-out, unless the pointer is on its way between the dot and its
   * dialog. The two don't touch, so the pointer crosses a few pixels of bare image between
   * them; the grace period lets it arrive, and the dialog's pointerenter cancels the close.
   * Closing on the crossing itself left the product card unreachable with a mouse.
   *
   * @param {PointerEvent} event - The pointerleave event.
   */
  #handlePointerLeave = (event) => {
    const { trigger, dialog } = this.refs;

    this.#clearHoverTimer();

    if (!dialog.open) return;

    const { relatedTarget } = event;
    const movingWithinHotspot =
      relatedTarget instanceof Node && (trigger.contains(relatedTarget) || dialog.contains(relatedTarget));

    if (movingWithinHotspot) return;

    this.#clearCloseTimer();
    this.#closeTimer = setTimeout(this.close, 300);
  };

  /**
   * @param {MouseEvent} event - The click event.
   */
  #lightDismiss = (event) => {
    const { trigger, dialog } = this.refs;

    if (!dialog.open) return;

    // The trigger's own handler owns the toggle.
    if (event.target instanceof Node && trigger.contains(event.target)) return;

    if (isClickedOutside(event, dialog)) this.close();
  };

  /**
   * A non-modal dialog does not dismiss on Escape by itself.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  };

  #removeDismissListeners() {
    document.removeEventListener('click', this.#lightDismiss);
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  #clearHoverTimer() {
    if (this.#hoverTimer == null) return;

    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = null;
  }

  #clearCloseTimer() {
    if (this.#closeTimer == null) return;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }

  /**
   * Releases the scroll lock, unless another hotspot's sheet still holds it.
   */
  #unlockScroll() {
    const stillOpen = document.querySelector('.shoppable-hotspot__dialog:modal');

    if (!stillOpen) document.documentElement.removeAttribute('scroll-lock');
  }
}

if (!customElements.get('shoppable-hotspot-component')) {
  customElements.define('shoppable-hotspot-component', ShoppableHotspotComponent);
}
